// Device context for error triage.
//
// Two-stage capture so the very first event is correctly tagged even if the
// user crashes before the native bridge resolves:
//   1. `is_native` is set SYNCHRONOUSLY at init from window.Capacitor —
//      no plugin call, no bundle cost on the web path.
//   2. Native-only extras (manufacturer, model, WebView version, app
//      version…) are filled ASYNCHRONOUSLY via @capacitor/device. The
//      plugin JS is dynamic-imported inside the isNative branch so web
//      bundles don't ship it.
//
// Callers should invoke `initDeviceContext()` once at reporter init and
// read `getDeviceContext()` synchronously at each event capture.

export interface DeviceContext {
  /** True inside the Capacitor APK/iOS app; false in every browser + PWA. */
  is_native: boolean
  /** Manufacturer slug from android.os.Build (e.g. "samsung", "xiaomi"). */
  device_manufacturer?: string
  /** Codename from android.os.Build.MODEL (e.g. "SM-S908B", "Pixel 7"). */
  device_model?: string
  /** OS version from android.os.Build — authoritative, not UA-derived. */
  device_os_version?: string
  /** Chromium version powering the WebView (e.g. "120.0.6099.144"). */
  webview_version?: string
  /** Matches versionName in build.gradle (e.g. "1.0.3"). */
  app_version?: string
  /** True on emulators — useful for filtering out QA noise. */
  is_virtual?: boolean
  /** Android API level (e.g. 34 for Android 14). */
  android_sdk_int?: number
}

let cache: DeviceContext = { is_native: false }

/** Synchronous read — safe to call at any point after `initDeviceContext()`. */
export function getDeviceContext(): DeviceContext {
  return cache
}

/**
 * Zero-cost native detection. The `Capacitor` global is injected by the
 * WebView only inside the packaged APK, so this correctly distinguishes:
 *   - Installed APK             → true
 *   - PWA installed on Android  → false (runs in Chrome/Samsung Internet)
 *   - Any mobile/desktop browser → false
 */
function isNativePlatformSync(): boolean {
  if (typeof window === "undefined") return false
  // @ts-ignore — Capacitor injects this global into the WebView.
  const cap = (window as any).Capacitor
  if (!cap) return false
  return typeof cap.isNativePlatform === "function" ? !!cap.isNativePlatform() : false
}

/**
 * Populate the context cache. Sync-tags `is_native` immediately (available
 * for event #1), then fills the granular fields asynchronously on native.
 * Safe to call more than once — no-op after the first resolution.
 */
export async function initDeviceContext(): Promise<void> {
  const isNative = isNativePlatformSync()
  // Always overwrite so subsequent init calls see the fresh sync value even
  // before the async fill completes.
  cache = { is_native: isNative }
  if (!isNative) return // Web/PWA: server-side UA parse already covers this.

  try {
    const { Device } = await import("@capacitor/device")
    const info = await Device.getInfo()

    // App version lives on @capacitor/app (versionName on Android,
    // CFBundleShortVersionString on iOS) — @capacitor/device does not expose it.
    // Isolated so a missing App bridge doesn't drop the device fields too.
    let appVersion: string | undefined
    try {
      const { App } = await import("@capacitor/app")
      const appInfo = await App.getInfo()
      appVersion = appInfo.version
    } catch {
      // App plugin unavailable — device fields still land without version.
    }

    cache = {
      is_native: true,
      device_manufacturer: info.manufacturer,
      device_model: info.model,
      device_os_version: info.osVersion,
      webview_version: info.webViewVersion,
      app_version: appVersion,
      is_virtual: info.isVirtual,
      android_sdk_int: info.androidSDKVersion,
    }
  } catch {
    // Plugin unavailable / bridge failure — leave is_native=true as-is so
    // triage still knows the event came from the APK, just without the
    // granular breakdown. Never throw from init.
  }
}
